import { authEndpoints } from "@/api/auth/auth.endpoints";
import { permissionsQueryOptions } from "@/api/permissions/permissions.api";
import NewPasswordRequired from "@/components/features/NewPasswordRequired/NewPasswordRequired";
import PasswordResetRequired from "@/components/features/NewPasswordRequired/PasswordResetMagicLink";
import VisibilityToggleIcon from "@/components/features/VisibilityToggleIcon/VisibilityToggleIcon";
import { Checkbox } from "@onengine/component-library";
import { Input } from "@/components/ui/Input/Input";
import { Password } from "@/components/ui/Password/Password";
import useAuth from "@/hooks/auth/useAuth";
import { getRequestScope } from "@/hooks/useRequestScope";
import { useDeleteAllFilterPreferences } from "@/hooks/useViewPreferences";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { useTableFilterBaseStore } from "@/store/tableFilterStore";
import { isAuthenticated } from "@/utils/authHelpers";
import { consumeReturnPath, toNavigateTarget } from "@/utils/postLoginReturn";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Card, Group, Text, Title } from "@mantine/core";
import { Alert, Spinner } from "@onengine/component-library";
import { IconLink } from "@tabler/icons-react";
import {
  Link,
  createFileRoute,
  getRouteApi,
  redirect,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import z from "zod";
import styles from "./login.module.scss";

const unauthenticatedRoute = getRouteApi("/_unauthenticated");

export const Route = createFileRoute("/_unauthenticated/login/")({
  beforeLoad: async () => {
    // If already authenticated, redirect to service providers
    if (isAuthenticated()) {
      throw redirect({ to: "/" });
    }
  },
  component: LoginRouteComponent,
});

const loginFormSchema = z.object({
  username: z.string().email("Please enter a valid email address"),
  password: z.string(),
  rememberMe: z.boolean().optional(),
});

// Infer TypeScript type from Zod schema
type TLoginFormData = z.infer<typeof loginFormSchema>;

const newPasswordFormSchema = z
  .object({
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(/^\S+$/, "Password must not contain spaces")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[0-9]/, "Password must contain at least one number"),
    confirmPassword: z
      .string()
      .min(8, { message: "Password must be at least 8 characters" })
      .regex(/^\S+$/, "Password must not contain spaces"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type TNewPasswordFormData = z.infer<typeof newPasswordFormSchema>;

const passwordResetFormSchema = z
  .object({
    verificationCode: z
      .string()
      .min(6, "Verification code must be at least 6 characters")
      .max(6, "Verification code must be 6 characters"),
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(/^\S+$/, "Password must not contain spaces")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[0-9]/, "Password must contain at least one number")
      .regex(/[^A-Za-z0-9]/, "Password must contain at least one special character"),
    confirmPassword: z
      .string()
      .min(8, { message: "Password must be at least 8 characters" })
      .regex(/^\S+$/, "Password must not contain spaces"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type TPasswordResetFormData = z.infer<typeof passwordResetFormSchema>;

function LoginRouteComponent() {
  const { navigate } = useRouter();
  const searchParams = useSearch({
    from: "/_unauthenticated/login/",
  }) as {
    status?: string;
    message?: string;
    challenge?: string;
    email?: string;
    cognitoUserName?: string;
    error?: string;
  };

  const { isSsoEnabled, providers } = unauthenticatedRoute.useLoaderData();

  const formRef = useRef<HTMLFormElement | null>(null);

  const {
    register: loginRegister,
    handleSubmit: loginHandleSubmit,
    formState: { errors: loginErrors },
    reset: loginReset,
    setValue,
    getValues,
    control,
    watch,
  } = useForm<TLoginFormData>({
    resolver: zodResolver(loginFormSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      username: "",
      password: "",
      rememberMe: false,
    },
  });

  const {
    register: newPasswordRegister,
    handleSubmit: newPasswordHandleSubmit,
    formState: { errors: newPasswordErrors },
    reset: newPasswordReset,
  } = useForm<TNewPasswordFormData>({
    resolver: zodResolver(newPasswordFormSchema),
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const {
    register: passwordResetRegister,
    handleSubmit: passwordResetHandleSubmit,
    formState: { errors: passwordResetErrors },
    reset: passwordResetReset,
  } = useForm<TPasswordResetFormData>({
    resolver: zodResolver(passwordResetFormSchema),
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const { signIn, completeNewPassword, confirmPasswordReset } = useAuth();
  const { mutate: deleteAllFilterPreferences } = useDeleteAllFilterPreferences();
  const resetAllFilters = useTableFilterBaseStore((state) => state.resetAllFilters);

  const [submitting, setSubmitting] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [challengeUsername, setChallengeUsername] = useState<string>("");
  const [challengeSession, setChallengeSession] = useState<string>("");
  const [newPasswordValue, setNewPasswordValue] = useState("");
  const [shouldShowNewPasswordRequired, setShouldShowNewPasswordRequired] = useState(false);
  const [passwordResetUsername, setPasswordResetUsername] = useState<string>("");
  const [passwordResetCodeDelivery, setPasswordResetCodeDelivery] = useState<{
    destination?: string;
    medium?: string;
  }>({});
  const [passwordResetValue, setPasswordResetValue] = useState("");
  const [shouldShowPasswordReset, setShouldShowPasswordReset] = useState(false);
  const [lockedUsers, setLockedUsers] = useState<Record<string, number>>({});
  const [hardLockedUsers, setHardLockedUsers] = useState<Record<string, number>>(() => {
    try {
      const stored = localStorage.getItem("login-hard-locked-users");
      if (!stored) return {};
      const locks = JSON.parse(stored) as Record<string, number>;
      const now = Date.now();
      return Object.fromEntries(Object.entries(locks).filter(([, t]) => t > now));
    } catch {
      return {};
    }
  });
  const [countdown, setCountdown] = useState<number>(0);
  const ssoError = searchParams.error === "sso_failed";

  const watchedUsername = watch("username")?.toLowerCase() ?? "";
  const isHardLocked =
    !!hardLockedUsers[watchedUsername] && hardLockedUsers[watchedUsername] > Date.now();
  const hardLockUntil = isHardLocked ? new Date(hardLockedUsers[watchedUsername]) : null;

  // sync browser autofill values into RHF
  const syncAutofillIntoRHF = () => {
    const form = formRef.current;
    if (!form) return;

    const pick = (name: "username" | "password") => {
      const el = form.querySelector<HTMLInputElement>(`input[name="${name}"]`);
      if (el && el.value && getValues(name) !== el.value) {
        setValue(name, el.value, { shouldDirty: true, shouldValidate: true });
      }
    };

    pick("username");
    pick("password");
  };

  // visibility check needs to be done for cross browser compatibility (safari, etc.)
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") syncAutofillIntoRHF();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // Load locked users from localStorage on mount and clear expired locks
  useEffect(() => {
    const storedLocks = localStorage.getItem("login-locked-users");
    if (storedLocks) {
      try {
        const locks = JSON.parse(storedLocks) as Record<string, number>;
        const now = Date.now();
        const validLocks: Record<string, number> = {};

        // Filter out expired locks
        Object.entries(locks).forEach(([username, unlockTime]) => {
          if (unlockTime > now) {
            validLocks[username] = unlockTime;
          }
        });

        setLockedUsers(validLocks);
      } catch (err) {
        console.error("Failed to parse locked users from localStorage", err);
      }
    }
  }, []);

  // Save locked users to localStorage whenever they change
  useEffect(() => {
    try {
      if (Object.keys(lockedUsers).length > 0) {
        localStorage.setItem("login-locked-users", JSON.stringify(lockedUsers));
      } else {
        localStorage.removeItem("login-locked-users");
      }
    } catch {
      // localStorage unavailable (e.g. Safari private mode) — lock lives in memory only
    }
  }, [lockedUsers]);

  useEffect(() => {
    try {
      if (Object.keys(hardLockedUsers).length > 0) {
        localStorage.setItem("login-hard-locked-users", JSON.stringify(hardLockedUsers));
      } else {
        localStorage.removeItem("login-hard-locked-users");
      }
    } catch {
      // localStorage unavailable (e.g. Safari private mode) — lock lives in memory only
    }
  }, [hardLockedUsers]);

  useEffect(() => {
    if (Object.keys(lockedUsers).length === 0) {
      setCountdown(0);
      return;
    }

    const timer = setInterval(() => {
      const username = getValues("username")?.toLowerCase();
      if (username && lockedUsers[username]) {
        const remaining = Math.max(0, Math.ceil((lockedUsers[username] - Date.now()) / 1000));
        setCountdown(remaining);

        if (remaining === 0) {
          setLockedUsers((prev) => {
            const updated = { ...prev };
            delete updated[username];
            return updated;
          });
        }
      } else {
        setCountdown(0);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [lockedUsers, getValues]);

  const formatCountdown = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  const formatLockDate = (date: Date): string =>
    date.toLocaleString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

  const handleSsoSignIn = async () => {
    const providerName = providers[0]?.providerName;
    if (!providerName) return;
    setSubmitting(true);
    try {
      // we need to redirect to the SSO initiate endpoint to get the redirect URL
      window.location.href = `${import.meta.env.VITE_BASE_URL}${authEndpoints.ssoInitiate}?provider=${encodeURIComponent(providerName)}`;
    } catch {
      setSubmitError("Failed to initiate SSO. Please try again.");
      setSubmitting(false);
    }
  };

  // Check for password reset challenge in URL params
  useEffect(() => {
    if (searchParams.challenge === "PASSWORD_RESET_REQUIRED" && searchParams.email) {
      setPasswordResetUsername(searchParams.email);
      setShouldShowPasswordReset(true);
      // Clear the query params from URL to avoid confusion
      navigate({
        to: "/login",
        search: {},
        replace: true,
      });
    }
  }, [searchParams.challenge, searchParams.email]);

  const onSubmit = async (data: TLoginFormData) => {
    const username = data.username.toLowerCase();

    if (hardLockedUsers[username] && hardLockedUsers[username] > Date.now()) return;

    if (lockedUsers[username]) {
      const remaining = Math.ceil((lockedUsers[username] - Date.now()) / 1000);
      if (remaining > 0) {
        setSubmitError(
          `Too many failed attempts. Please try again in ${formatCountdown(remaining)}.`,
        );
        return;
      }
    }

    setSubmitError(null);
    setSubmitting(true);
    try {
      const res = await signIn(
        data.username.toLowerCase(),
        data.password,
        Boolean(data.rememberMe),
      );

      if ((res as any).challenge === "NEW_PASSWORD_REQUIRED") {
        const { username, cognitoSession } = res as any;
        setChallengeUsername(username);
        setChallengeSession(cognitoSession);
        setShouldShowNewPasswordRequired(true);
        // Do NOT reset username here; we still need it for context in the new password form
        return;
      }

      if ((res as any).challenge === "PASSWORD_RESET_REQUIRED") {
        const { username, codeDeliveryDetails } = res as any;
        setPasswordResetUsername(username);
        setPasswordResetCodeDelivery({
          destination: codeDeliveryDetails?.Destination,
          medium: codeDeliveryDetails?.DeliveryMedium,
        });
        setShouldShowPasswordReset(true);
        return;
      }

      // Normal success, reset the login form and navigate to the home page
      loginReset();
      resetAllFilters();
      deleteAllFilterPreferences();
      setBootstrapping(true);
      try {
        await queryClient.ensureQueryData(permissionsQueryOptions(getRequestScope()));
      } catch {
        // beforeLoad will re-fetch if needed; don't block navigation on prefetch failure
      }
      const returnPath = consumeReturnPath();
      await navigate(returnPath ? toNavigateTarget(returnPath) : { to: "/" });
    } catch (err: any) {
      setBootstrapping(false);
      const apiError = err?.response?.data?.error;

      if (apiError?.code === "ACCOUNT_LOCKED") {
        const unlockTime = new Date(apiError?.details?.[0]?.lockedUntil).getTime();
        setHardLockedUsers((prev) => ({ ...prev, [username]: unlockTime }));
        setSubmitError(null);
      } else if (apiError?.code === "LOGIN_BLOCKED") {
        const unlockTime = new Date(apiError?.details?.[0]?.shortBlockUntil).getTime();
        const remaining = Math.max(0, Math.ceil((unlockTime - Date.now()) / 1000));
        setLockedUsers((prev) => ({ ...prev, [username]: unlockTime }));
        setCountdown(remaining);
        setSubmitError(
          apiError.message ?? "Too many failed login attempts. Please try again later.",
        );
      } else if (
        (err?.__type === "NotAuthorizedException" ||
          err?.code === "NotAuthorizedException" ||
          err?.name === "NotAuthorizedException") &&
        (err?.message === "Password attempts exceeded" ||
          err?.message?.toLowerCase().includes("password attempts exceeded"))
      ) {
        const unlockTime = Date.now() + 5 * 60 * 1000;
        const remaining = Math.max(0, Math.ceil((unlockTime - Date.now()) / 1000));
        setLockedUsers((prev) => ({ ...prev, [username]: unlockTime }));
        setCountdown(remaining);
        setSubmitError(`Too many failed attempts. Please try again in 5 minutes.`);
      }
      // Check if error is PasswordResetRequiredException
      else if (
        err?.name === "PasswordResetRequiredException" ||
        err?.code === "PasswordResetRequiredException"
      ) {
        // Backend has already triggered forgotPassword and sent the code
        // Extract challenge data from error or use defaults
        setPasswordResetUsername(data.username.toLowerCase());
        setPasswordResetCodeDelivery({
          destination: err?.codeDeliveryDetails?.Destination || err?.Destination,
          medium: err?.codeDeliveryDetails?.DeliveryMedium || err?.DeliveryMedium,
        });
        setShouldShowPasswordReset(true);
        setSubmitError(null); // Clear any error message
      } else {
        setSubmitError(err?.response?.data?.error?.message ?? "Login failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onCompleteNewPassword = async (data: TNewPasswordFormData) => {
    if (!challengeUsername || !challengeSession) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      await completeNewPassword(challengeUsername, data.password, challengeSession);
      newPasswordReset();
      loginReset(); // clear login form too
      resetAllFilters();
      deleteAllFilterPreferences();
      setBootstrapping(true);
      try {
        await queryClient.ensureQueryData(permissionsQueryOptions(getRequestScope()));
      } catch {
        // beforeLoad will re-fetch if needed; don't block navigation on prefetch failure
      }
      const returnPath = consumeReturnPath();
      await navigate(returnPath ? toNavigateTarget(returnPath) : { to: "/" });
    } catch (err: any) {
      setBootstrapping(false);
      setSubmitError(err?.message ?? "Failed to set new password");
    } finally {
      setSubmitting(false);
    }
  };

  const onConfirmPasswordReset = async (data: TPasswordResetFormData) => {
    if (!passwordResetUsername) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      await confirmPasswordReset(passwordResetUsername, data.verificationCode, data.password);

      passwordResetReset();
      loginReset();
      setShouldShowPasswordReset(false);

      navigate({
        to: "/login",
        search: {
          status: "reset-success",
          message: "Password reset successful. Please log in with your new password.",
        },
      });
    } catch {
      setSubmitError("Incorrect verification code");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {bootstrapping && (
        <Box
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "var(--mantine-color-body, #fff)",
            zIndex: 9999,
          }}
        >
          <Spinner size="large" />
        </Box>
      )}
      {!shouldShowNewPasswordRequired && !shouldShowPasswordReset ? (
        isSsoEnabled ? (
          <Box component="div" className={styles.login_container}>
            <Title className={styles.login_header}>Login</Title>
            <Card className={styles.login_body} radius={8}>
              {ssoError && (
                <Alert
                  variant="strong"
                  type="danger"
                  title="Authentication failed, please try again."
                />
              )}
              <Text className={styles.sso_description}>
                SSO is enabled for your organization. Click below to log in.
              </Text>
              <Button
                data-testid="sso-login-btn"
                onClick={handleSsoSignIn}
                className={styles.login_button}
                radius={8}
                leftSection={<IconLink size={18} />}
                disabled={submitting}
                loading={submitting}
              >
                <Text className={styles.signIn_text}>Sign In with SSO</Text>
              </Button>
              <Text className={styles.sso_help_text}>
                Need help? Contact your IT administrator.
              </Text>
            </Card>
          </Box>
        ) : (
          <Box component="div" className={styles.login_container}>
            <Title className={styles.login_header}>Login</Title>
            <Card className={styles.login_body} radius={8}>
              {isHardLocked && hardLockUntil && (
                <Alert
                  variant="strong"
                  type="danger"
                  title={`Your account has been locked due to multiple failed login attempts. You may sign in again after ${formatLockDate(hardLockUntil)}.`}
                />
              )}

              {submitError && <Alert variant="strong" type="danger" title={submitError ?? ""} />}

              {searchParams.status === "reset-success" && (
                <Alert variant="strong" type="success" title={searchParams?.message as string} />
              )}

              <form
                ref={formRef}
                onSubmit={(e) => {
                  syncAutofillIntoRHF();
                  return loginHandleSubmit(onSubmit)(e);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    syncAutofillIntoRHF();
                    loginHandleSubmit(onSubmit)();
                  }
                }}
                className={styles.login_form}
                autoComplete="on"
              >
                <Input
                  label="Username"
                  placeholder="Enter your email"
                  type="email"
                  required
                  register={loginRegister("username")}
                  size="large"
                  error={loginErrors.username?.message}
                  dataTestId="username"
                  width={"100%"}
                  autoComplete="username"
                />

                <Password
                  label="Password"
                  placeholder="Enter your password"
                  type="password"
                  register={loginRegister("password")}
                  size="large"
                  error={loginErrors.password?.message}
                  dataTestId="password"
                  visibilityToggleIcon={VisibilityToggleIcon}
                  width={"100%"}
                  autoComplete="current-password"
                />

                <Group gap={2} justify="space-between">
                  <Group gap={8}>
                    <Controller
                      name="rememberMe"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          data-testid="rememberMe"
                          name={field.name}
                          onBlur={field.onBlur}
                          state={field.value ? "selected" : "deselected"}
                          onValueChange={(checked) => field.onChange(checked)}
                        />
                      )}
                    />
                    <Text
                      className={styles.input_field}
                      style={{
                        color: "var(--text-default)",
                        marginTop: 0,
                        height: "fit-content",
                      }}
                    >
                      Remember Me
                    </Text>
                  </Group>
                  <Link
                    data-testid="reset-password-link"
                    to={"/forgot-password"}
                    className={styles.forgot_password_link}
                  >
                    Forgot password?
                  </Link>
                </Group>

                <Button
                  data-testid="login-btn"
                  type="submit"
                  loading={submitting}
                  disabled={isHardLocked || countdown > 0}
                  className={styles.login_button}
                  radius={8}
                  classNames={{}}
                >
                  <Text className={styles.signIn_text}>
                    {isHardLocked
                      ? "Account Locked"
                      : countdown > 0
                        ? `Try again in ${formatCountdown(countdown)}`
                        : "Sign In"}
                  </Text>
                </Button>
              </form>
            </Card>
          </Box>
        )
      ) : shouldShowNewPasswordRequired ? (
        <NewPasswordRequired
          handleSubmit={newPasswordHandleSubmit}
          onSubmit={onCompleteNewPassword}
          register={newPasswordRegister}
          errors={newPasswordErrors}
          value={newPasswordValue}
          handleChange={setNewPasswordValue}
          isPending={submitting}
        />
      ) : (
        <PasswordResetRequired
          handleSubmit={passwordResetHandleSubmit}
          onSubmit={onConfirmPasswordReset}
          register={passwordResetRegister}
          errors={passwordResetErrors}
          passwordValue={passwordResetValue}
          handlePasswordChange={setPasswordResetValue}
          isPending={submitting}
          deliveryDestination={passwordResetCodeDelivery.destination}
          deliveryMedium={passwordResetCodeDelivery.medium}
          submitError={submitError}
        />
      )}
    </>
  );
}
